import React, { useEffect, useMemo, useState } from 'react'
import { enqueueSnackbar } from 'notistack'
import { PT, SECTION_COLORS as PT_SECTION_COLORS } from '../../utils/productionTools/themeColors'
import { CURRENCY, SECTION_LABELS } from '../../utils/filmBudget/constants'
import { fmt } from '../../utils/filmBudget/calculator'
import { downloadAiToolPdf } from '../../utils/aiTool/pdfDownload'
import { formatAiTextLine } from '../../utils/filmBudget/formatAiText'
import AiToolDownloadPdfButton from '../productionTools/AiToolDownloadPdfButton'

const SECTION_COLORS = PT_SECTION_COLORS

const EXPLAINER_CARDS = [
  {
    color: PT.info,
    label: 'ATL — Above the Line',
    title: 'The Creative Package',
    text: "Director, Writer, Producers, Principal Cast. Fixed costs negotiated before production begins. Investors look at ATL to assess the film's commercial viability.",
    healthy: 'Healthy: 25–35% of total',
  },
  {
    color: PT.infoLight,
    label: 'BTL — Below the Line',
    title: 'The Execution Budget',
    text: 'Crew, equipment, locations, art department, transport, catering. Variable costs that scale with shoot length. This is where films are actually made.',
    healthy: 'Healthy: 40–50% of total',
  },
  {
    color: PT.success,
    label: 'Post Production',
    title: 'The Finishing Process',
    text: 'Editing, color grading, sound design, VFX, music, delivery masters. Often underbudgeted by first-time producers. Always allocate before locking budget.',
    healthy: 'Healthy: 15–25% of total',
  },
  {
    color: PT.warning,
    label: 'Other / Fringes',
    title: 'The Safety Net',
    text: 'Insurance, legal, contingency reserve. The 10% contingency is non-negotiable — bond companies require it. Never spend it unless genuinely unavoidable.',
    healthy: 'Healthy: 8–12% of total',
  },
]

const TOPSHEET_SECTIONS = [
  {
    key: 'atl',
    label: 'Above the Line (ATL)',
    sub: 'Story · Director · Producers · Cast',
  },
  {
    key: 'btl',
    label: 'Below the Line (BTL)',
    sub: 'Crew · Equipment · Locations · Art · Transport',
  },
  {
    key: 'post',
    label: 'Post Production',
    sub: 'Edit · Color · Sound · VFX · Music · Delivery',
  },
  {
    key: 'other',
    label: 'Other / Fringes',
    sub: 'Insurance · Legal · Contingency · Bond',
  },
]

const FilmBudgetBreakdownResultsView = ({
  budget,
  inputs,
  investorStmt,
  intelItems,
  aiContent,
  aiError = '',
  market,
  onReset,
}) => {
  const [openSections, setOpenSections] = useState({
    atl: true,
    btl: true,
    post: false,
    other: false,
  })
  const [animateBars, setAnimateBars] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)

  const currency = CURRENCY[market] || CURRENCY.India

  useEffect(() => {
    const timer = setTimeout(() => setAnimateBars(true), 100)
    return () => clearTimeout(timer)
  }, [])

  const healthFlags = useMemo(
    () => [
      {
        icon: '📊',
        label: 'ATL Ratio',
        value: `${Math.round(budget.atlPct * 100)}%`,
        note: budget.health.atlWarning
          ? 'Above 50% — review star costs'
          : 'Within healthy range (25-35%)',
        cls: budget.health.atlWarning
          ? 'flag-danger'
          : budget.atlPct > 0.38
            ? 'flag-warn'
            : 'flag-ok',
      },
      {
        icon: '🛡️',
        label: 'Contingency',
        value: `${budget.health.contingencyPct.toFixed(1)}%`,
        note: budget.health.contingencyOk
          ? 'Meets industry 10% minimum'
          : 'Below recommended 10%',
        cls: budget.health.contingencyOk ? 'flag-ok' : 'flag-danger',
      },
      {
        icon: '⚙️',
        label: 'BTL Ratio',
        value: `${Math.round(budget.btlPct * 100)}%`,
        note: budget.health.btlWarning
          ? 'Below 25% — production may be under-resourced'
          : 'Production adequately resourced',
        cls: budget.health.btlWarning ? 'flag-warn' : 'flag-ok',
      },
      {
        icon: '📅',
        label: 'Cost Per Day',
        value: fmt(budget.btlTotal / inputs.shootDays, market),
        note: `Based on ${inputs.shootDays} shoot days`,
        cls: 'flag-ok',
      },
    ],
    [budget, inputs.shootDays, market],
  )

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleDownloadPdf = async () => {
    setPdfLoading(true)
    try {
      await downloadAiToolPdf(inputs.aiuId, inputs.projTitle || 'budget', {
        exportId: 'budget-pdf-export',
      })
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to export PDF. Please try again.',
        { variant: 'error' },
      )
    } finally {
      setPdfLoading(false)
    }
  }

  return (
    <div className="res-wrap on">
      <div className="rhead">
        <div>
          <div className="rtitle">{inputs.projTitle} — Budget Breakdown</div>
          <div className="rsub">
            {inputs.format} · {inputs.genre} · {inputs.shootDays} shoot days
          </div>
          <div className="rmetas">
            {[
              inputs.market,
              `${currency.sym} ${currency.code}`,
              `${fmt(budget.total, market)} total`,
              inputs.distrib,
            ].map((meta) => (
              <span key={meta} className="rmeta">
                {meta}
              </span>
            ))}
          </div>
        </div>
        <div className="abtns">
          <AiToolDownloadPdfButton
            loading={pdfLoading}
            onClick={handleDownloadPdf}
          >
            Export PDF
          </AiToolDownloadPdfButton>
          <button type="button" className="btn-new" onClick={onReset}>
            New Budget
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div
          className="intel-title"
          style={{
            fontSize: 9,
            letterSpacing: 4,
            textTransform: 'uppercase',
            color: 'var(--gold)',
            marginBottom: 16,
          }}
        >
          Understanding Your Budget Structure
        </div>
        <div className="explainer-grid">
          {EXPLAINER_CARDS.map((card) => (
            <div
              key={card.label}
              className="explainer-card"
              style={{ borderTop: `3px solid ${card.color}` }}
            >
              <div
                style={{
                  fontSize: 9,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  color: card.color,
                  marginBottom: 6,
                }}
              >
                {card.label}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--white)',
                  fontWeight: 600,
                  marginBottom: 6,
                }}
              >
                {card.title}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--muted)',
                  lineHeight: 1.6,
                }}
              >
                {card.text}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: card.color,
                  marginTop: 8,
                  fontWeight: 600,
                }}
              >
                {card.healthy}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="topsheet">
        <div className="topsheet-header">
          <div>
            <div className="topsheet-title">Topsheet Summary</div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--muted)',
                marginTop: 3,
                letterSpacing: 0.5,
              }}
            >
              What investors and distributors see first
            </div>
          </div>
          <div className="topsheet-total">
            {fmt(budget.total, market)}
            <span>Total Negative Cost</span>
          </div>
        </div>
        {TOPSHEET_SECTIONS.map((sec) => {
          const pct = Math.round(budget[`${sec.key}Pct`] * 100)
          const total = budget[`${sec.key}Total`]
          return (
            <div key={sec.key} className="ts-row">
              <div className="ts-cat">
                <div
                  className="ts-dot"
                  style={{ background: SECTION_COLORS[sec.key] }}
                />
                <div>
                  <div className="ts-name">{sec.label}</div>
                  <div className="ts-sub">{sec.sub}</div>
                </div>
              </div>
              <div className="ts-bar-wrap">
                <div className="ts-bar-track">
                  <div
                    className="ts-bar-fill"
                    style={{
                      width: animateBars ? `${pct}%` : '0%',
                      background: SECTION_COLORS[sec.key],
                    }}
                  />
                </div>
              </div>
              <div className="ts-pct">{pct}%</div>
              <div className="ts-amt">{fmt(total, market)}</div>
            </div>
          )
        })}
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="intel-title" style={{ marginBottom: 12 }}>
          Budget Distribution
        </div>
        <div className="dist-chart">
          {TOPSHEET_SECTIONS.map((sec) => (
            <div
              key={sec.key}
              className="dc-seg"
              style={{
                width: `${budget[`${sec.key}Pct`] * 100}%`,
                background: SECTION_COLORS[sec.key],
                flexShrink: 0,
              }}
            />
          ))}
        </div>
        <div className="dist-legend">
          {TOPSHEET_SECTIONS.map((sec) => (
            <div key={sec.key} className="dl-item">
              <div
                className="dl-dot"
                style={{ background: SECTION_COLORS[sec.key] }}
              />
              {sec.label} {Math.round(budget[`${sec.key}Pct`] * 100)}%
            </div>
          ))}
        </div>
      </div>

      <div className="health-row">
        {healthFlags.map((flag) => (
          <div key={flag.label} className={`hflag ${flag.cls}`}>
            <div className="hflag-icon">{flag.icon}</div>
            <div className="hflag-label">{flag.label}</div>
            <div className="hflag-value">{flag.value}</div>
            <div className="hflag-note">{flag.note}</div>
          </div>
        ))}
      </div>

      <div className="investor-stmt">
        <div className="is-title">Financier Intelligence</div>
        <div className="is-text">{investorStmt}</div>
      </div>

      <div
        className="intel-panel"
        style={{ borderColor: 'rgba(232,184,75,0.3)' }}
      >
        <div className="intel-title">🎬 Line Producer Commentary</div>
        {aiError && (
          <div
            style={{
              textAlign: 'center',
              padding: 20,
              color: 'var(--muted)',
              fontSize: 13,
            }}
          >
            {aiError}
          </div>
        )}
        {aiContent && !aiError && (
          <div
            className="ai-content"
            style={{
              fontSize: 13,
              color: 'var(--muted)',
              lineHeight: 1.9,
            }}
          >
            {aiContent
              .split('\n')
              .filter((line) => line.trim())
              .map((line, i) => (
                <p
                  key={i}
                  dangerouslySetInnerHTML={{
                    __html: formatAiTextLine(line),
                  }}
                />
              ))}
          </div>
        )}
      </div>

      {['atl', 'btl', 'post', 'other'].map((key) => {
        const sectionData = budget[key]
        const color = SECTION_COLORS[key]
        const label = SECTION_LABELS[key]
        return (
          <div
            key={key}
            className={`section-block ${openSections[key] ? 'open' : ''}`}
          >
            <div
              className="section-header"
              onClick={() => toggleSection(key)}
              onKeyDown={(e) => e.key === 'Enter' && toggleSection(key)}
              role="button"
              tabIndex={0}
            >
              <div className="sh-color" style={{ background: color }} />
              <div className="sh-info">
                <div className="sh-cat" style={{ color }}>
                  {key.toUpperCase()}
                </div>
                <div className="sh-name" style={{ color }}>
                  {label}
                </div>
              </div>
              <div>
                <span className="sh-total">{fmt(sectionData.total, market)}</span>
                <span className="sh-pct">
                  {Math.round((sectionData.total / budget.total) * 100)}%
                </span>
              </div>
              <div className="sh-toggle">▾</div>
            </div>
            <div className="section-body">
              {sectionData.items.map((item) => (
                <div key={item.name} className="line-item">
                  <div className="li-name">
                    {item.name}
                    {item.flag && (
                      <span className={`li-flag flag-${item.flag}`}>
                        {item.flag.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="li-detail">{item.detail}</div>
                  <div className="li-amt">{fmt(item.amount, market)}</div>
                </div>
              ))}
            </div>
          </div>
        )
      })}

      <div className="intel-panel">
        <div className="intel-title">Budget Intelligence Report</div>
        <div className="intel-grid">
          {intelItems.map((item) => (
            <div key={item.title} className={item.cls}>
              <div className="intel-item-title">{item.title}</div>
              <div className="intel-item-text">{item.text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default FilmBudgetBreakdownResultsView
